import { computed, toValue } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import axios from 'axios';

import { queryClient } from '@/queries/_shared/queryClient';
import { queryKeys } from '@/queries/_shared/queryKeys';
import http from '@/services/http';

const SDOT_UPLOAD_URL = String(import.meta.env.VITE_SDOT_UPLOAD_URL || '');

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) return data.body;
  return data;
}

function getLangHeader() {
  return localStorage.getItem('translation') === 'kz' ? 'kk-KZ' : 'ru-RU';
}

function normalizeTs(value) {
  const ts = Number(value || 0);
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  return Math.floor(ts);
}

async function fetchAllTasksPlans(startTs, endTs, groupId, pupilId) {
  const response = await http.get('/lplans', {
    params: {
      start_ts: normalizeTs(startTs),
      end_ts: normalizeTs(endTs),
      group_id: Number(groupId || 0),
      p_id: Number(pupilId || 0),
      pupil_id: Number(pupilId || 0),
      expand: 'ltasksonly',
      'per-page': 1000
    }
  });
  return unwrapBody(response.data) || [];
}

async function createTaskAnswer(payload) {
  const response = await http.post('/plans/tasks-results', payload);
  return unwrapBody(response.data);
}

async function updateTaskAnswer({ answerId, payload }) {
  const response = await http.put(`/plans/tasks-results/${Number(answerId || 0)}`, payload);
  return unwrapBody(response.data);
}

async function uploadTaskFile({ file, onProgress }) {
  if (!SDOT_UPLOAD_URL) {
    throw new Error('VITE_SDOT_UPLOAD_URL is not configured');
  }

  const formData = new FormData();
  formData.append('Upload[finished]', false);
  formData.append('Upload[percent]', 0);
  formData.append('Upload[error]', false);
  formData.append('Upload[unzip]', 0);
  formData.append('yModel', 'Upload');
  formData.append('Upload[name]', file.name);
  formData.append('Upload[file]', file);

  const response = await axios.post(SDOT_UPLOAD_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-lang': getLangHeader()
    },
    onUploadProgress: (evt) => {
      if (typeof onProgress !== 'function') return;
      const total = Number(evt?.total || 0);
      const loaded = Number(evt?.loaded || 0);
      if (!total) return;
      onProgress(Math.round((loaded * 100) / total));
    }
  });

  const uploaded = response?.data || {};
  const hasProtocol = /^https?:\/\//i.test(uploaded.url || '');
  if (uploaded.url && !hasProtocol) {
    uploaded.url = `https://${uploaded.url}`;
  }
  return uploaded;
}

export function useAllTasksPlansQuery(startTs, endTs, groupId, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.allTasks.plans(toValue(startTs), toValue(endTs), toValue(groupId), toValue(pupilId))),
    queryFn: () => fetchAllTasksPlans(toValue(startTs), toValue(endTs), toValue(groupId), toValue(pupilId)),
    enabled: computed(
      () =>
        normalizeTs(toValue(startTs)) > 0 &&
        normalizeTs(toValue(endTs)) > 0 &&
        Number(toValue(groupId) || 0) > 0 &&
        Number(toValue(pupilId) || 0) > 0
    ),
    ...options,
  });
}

export function useCreateTaskAnswerMutation(options = {}) {
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: createTaskAnswer,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['allTasks'], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['task', 'tasks'], refetchType: 'active' })
      ]);

      if (typeof onSuccess === 'function') {
        return onSuccess(...args);
      }

      return undefined;
    },
    ...restOptions
  });
}

export function useUpdateTaskAnswerMutation(options = {}) {
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: updateTaskAnswer,
    onSuccess: async (...args) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['allTasks'], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['task', 'tasks'], refetchType: 'active' })
      ]);

      if (typeof onSuccess === 'function') {
        return onSuccess(...args);
      }

      return undefined;
    },
    ...restOptions
  });
}

export function useUploadTaskFileMutation(options = {}) {
  return useMutation({
    mutationFn: uploadTaskFile,
    ...options
  });
}

export { fetchAllTasksPlans };

